import React, { FC, ReactNode, useEffect, useState } from "react";
import ethereumProviderGuard from "app/utils/ethereum-provider-guard";

interface WalletProviderGuardProps {
  children: ReactNode;
}

interface WalletProviderGuardState {
  hasError: boolean;
  errorMessage: string;
  isRecovering: boolean;
}

/**
 * WalletProviderGuard component that wraps the app to handle wallet provider conflicts
 * and prevent ethereum provider redefinition errors
 */
const WalletProviderGuard: FC<WalletProviderGuardProps> = ({ children }) => {
  const [state, setState] = useState<WalletProviderGuardState>({
    hasError: false,
    errorMessage: "",
    isRecovering: false,
  });

  useEffect(() => {
    // Initialize ethereum provider guard on mount
    console.log(
      "WalletProviderGuard: Initializing ethereum provider protection..."
    );

    // Set up error handlers for provider conflicts
    const handleProviderError = (event: ErrorEvent) => {
      const error = event.error || event.message;
      const errorString = error?.toString() || "";

      // Check for ethereum provider conflicts (including OKX wallet conflicts)
      if (
        errorString.includes("Cannot redefine property: ethereum") ||
        errorString.includes("Cannot set property ethereum") ||
        errorString.includes("ethereum of #<Window> which has only a getter") ||
        errorString.includes(
          "MetaMask encountered an error setting the global Ethereum provider"
        ) ||
        errorString.includes("OKX Wallet") ||
        errorString.includes("okxwallet") ||
        errorString.includes("Multiple wallet extensions") ||
        errorString.includes("Provider injection conflict")
      ) {
        console.warn(
          "WalletProviderGuard: Detected ethereum provider conflict, handling gracefully..."
        );

        setState({
          hasError: true,
          errorMessage:
            "Multiple wallet extensions detected. Please disable conflicting wallet extensions or refresh the page.",
          isRecovering: false,
        });

        // Prevent the error from propagating
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // Handle stream disconnection errors
      if (
        errorString.includes(
          "Cannot call write after a stream was destroyed"
        ) ||
        errorString.includes("stream was destroyed") ||
        errorString.includes("Lost connection to") ||
        errorString.includes("Connection channel closed")
      ) {
        console.warn(
          "WalletProviderGuard: Detected wallet stream disconnection, suppressing error..."
        );

        // Prevent the error from propagating but don't show error UI for stream issues
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorString = error?.toString() || "";

      if (
        errorString.includes("Cannot redefine property: ethereum") ||
        errorString.includes("Cannot set property ethereum") ||
        errorString.includes("getChainId is not a function")
      ) {
        console.warn(
          "WalletProviderGuard: Detected wallet provider promise rejection, handling gracefully..."
        );

        setState({
          hasError: true,
          errorMessage:
            "Wallet connection issue detected. Please try refreshing the page or switching wallet extensions.",
          isRecovering: false,
        });

        // Prevent the error from propagating
        event.preventDefault();
        return false;
      }

      // Handle stream disconnection promise rejections
      if (
        errorString.includes("stream was destroyed") ||
        errorString.includes("Lost connection") ||
        errorString.includes("Connection channel closed") ||
        errorString.includes("Unexpected error")
      ) {
        console.warn(
          "WalletProviderGuard: Detected wallet stream promise rejection, suppressing error..."
        );

        // Prevent the error from propagating but don't show error UI
        event.preventDefault();
        return false;
      }
    };

    // Add global error handlers
    window.addEventListener("error", handleProviderError, true);
    window.addEventListener(
      "unhandledrejection",
      handleUnhandledRejection,
      true
    );

    // Cleanup
    return () => {
      window.removeEventListener("error", handleProviderError, true);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
        true
      );
    };
  }, []);

  const handleRecovery = () => {
    setState((prev) => ({ ...prev, isRecovering: true }));

    // Reset ethereum provider guard
    ethereumProviderGuard.reset();

    // Reload the page after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleDismiss = () => {
    setState({
      hasError: false,
      errorMessage: "",
      isRecovering: false,
    });
  };

  if (state.hasError) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
          color: "white",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div
          style={{
            backgroundColor: "#1a1a1a",
            padding: "2rem",
            borderRadius: "8px",
            maxWidth: "500px",
            textAlign: "center",
            border: "1px solid #333",
          }}
        >
          <h2 style={{ color: "#ff6b6b", marginBottom: "1rem" }}>
            Wallet Extension Conflict Detected
          </h2>
          <p style={{ marginBottom: "1.5rem", lineHeight: "1.5" }}>
            {state.errorMessage}
          </p>
          <div
            style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
          >
            <button
              onClick={handleRecovery}
              disabled={state.isRecovering}
              style={{
                padding: "0.75rem 1.5rem",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: state.isRecovering ? "not-allowed" : "pointer",
                opacity: state.isRecovering ? 0.6 : 1,
              }}
            >
              {state.isRecovering ? "Refreshing..." : "Refresh Page"}
            </button>
            <button
              onClick={handleDismiss}
              style={{
                padding: "0.75rem 1.5rem",
                backgroundColor: "#666",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Continue Anyway
            </button>
          </div>
          <p
            style={{
              marginTop: "1rem",
              fontSize: "0.9rem",
              color: "#ccc",
              lineHeight: "1.4",
            }}
          >
            <strong>Tip:</strong> To prevent this issue, disable one of your
            wallet extensions (MetaMask, Phantom, Coinbase Wallet, etc.) and
            refresh the page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default WalletProviderGuard;
